import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { flagCode } from '../../utils/flags';
import { countryPath, dialCodeFor } from '../../utils/countryPage';
import { groupColor } from '../../utils/numberGroups';
import { ADDRESS_SHORT, PUBLIC_GROUPS, groupsOf, orderUrl } from '../../utils/numberInfo';
import NumberInfoDialog, { DocsBadge } from './NumberInfoDialog';

/** Search helper shared by the lists: country name or dialling code ('+49', '49'). */
export const matchesCountry = (c, q) => {
  const t = (q || '').trim().toLowerCase();
  if (!t) return true;
  const dial = (dialCodeFor(c.country) || '').replace(/\s/g, '');
  const digits = t.replace(/^\+/, '').replace(/\s/g, '');
  return c.country.toLowerCase().includes(t) || (/^\d+$/.test(digits) && dial.slice(1).startsWith(digits));
};

const Pill = ({ k, label, on }) => (
  <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold leading-4 ring-1 ${on ? groupColor(k).badge : 'bg-gray-50 text-gray-300 ring-gray-100'}`}>
    {label}{!on && <span className="sr-only"> (not available)</span>}
  </span>
);

/** Small results line above a list: "N results · Tap Info for full number details". */
export const ResultsLine = ({ count, loading, total, noun = 'countries' }) => (
  <p className="text-sm text-gray-600" aria-live="polite">
    {loading ? 'Loading coverage…' : (
      <>
        <span className="font-semibold text-[#0a1f2e]">{count} {count === 1 ? 'result' : 'results'}</span>
        {total !== undefined && total !== count ? ` of ${total} ${noun}` : ''}
        {' · Prices shown when you order · Tap '}<span className="font-semibold text-[#0a1f2e]">Info</span>{' for full number details'}
      </>
    )}
  </p>
);

/**
 * Country rows with number-type pills, the address rule under the name, a documents badge and Info / Order buttons.
 * group: the number type the page is about (preselected in Info and Order); onEnquire(country, type) opens the
 * page's enquiry form for visitors without a portal account.
 */
const NumberCountryTable = ({ countries, group, onEnquire, pageSize = 25 }) => {
  const [info, setInfo] = useState({ open: false, country: null, group: null });
  const [all, setAll] = useState(false);
  const rows = all ? countries : countries.slice(0, pageSize);

  return (
    <div>
      <div className="hidden grid-cols-[minmax(0,1.3fr)_minmax(0,1.6fr)_minmax(0,0.9fr)_auto] gap-4 border-b border-gray-200 px-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 lg:grid" aria-hidden="true">
        <span>Country</span><span>Number types</span><span>Documents</span><span className="w-[164px]" />
      </div>
      <ul className="divide-y divide-gray-100">
        {rows.map((c) => {
          const code = flagCode(c);
          const have = groupsOf(c);
          const pick = group && have.includes(group) ? group : null;
          return (
            <li key={c.country} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-1 py-3 sm:px-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1.6fr)_minmax(0,0.9fr)_auto] lg:gap-4">
              <div className="flex min-w-0 items-center gap-3">
                {code ? <img src={`https://flagcdn.com/w40/${code}.png`} alt="" width="28" height="20" loading="lazy" className="h-5 w-7 shrink-0 rounded-sm object-cover shadow-sm" />
                  : <span className="h-5 w-7 shrink-0 rounded-sm bg-gray-100" aria-hidden="true" />}
                <div className="min-w-0">
                  <Link to={countryPath(c.country)} className="block truncate font-semibold text-[#0a1f2e] hover:underline">{c.country}</Link>
                  {ADDRESS_SHORT[c.address] && <span className="block text-xs text-gray-500">{ADDRESS_SHORT[c.address]}</span>}
                </div>
              </div>
              <div className="col-span-2 row-start-2 flex flex-wrap gap-1 lg:col-span-1 lg:row-start-auto" aria-label={`Number types in ${c.country}`}>
                {PUBLIC_GROUPS.map((g) => <Pill key={g.key} k={g.key} label={g.label} on={have.includes(g.key)} />)}
              </div>
              <div className="col-span-2 row-start-3 lg:col-span-1 lg:row-start-auto">
                {c.documents ? <DocsBadge state={c.documents} size="sm" /> : <span className="hidden text-xs text-gray-400 lg:inline">Confirmed on order</span>}
              </div>
              <div className="col-start-2 row-start-1 flex items-center justify-end gap-2 lg:col-start-auto lg:row-start-auto">
                <button type="button" onClick={() => setInfo({ open: true, country: c.country, group: pick })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-[#0a1f2e] transition hover:border-[#0a1f2e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff00]">
                  <Info className="h-4 w-4" aria-hidden="true" />Info<span className="sr-only"> about {c.country} numbers</span>
                </button>
                <a href={orderUrl(c.country, pick)}
                  className="inline-flex items-center rounded-full bg-[#0a1f2e] px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-[#12324a] hover:text-[#c8ff00] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8ff00] focus-visible:ring-offset-1">
                  Order<span className="sr-only"> {c.country} numbers</span>
                </a>
              </div>
            </li>
          );
        })}
      </ul>
      {!all && countries.length > pageSize && (
        <div className="mt-4 text-center">
          <button type="button" onClick={() => setAll(true)} className="rounded-full border border-[#0a1f2e] px-5 py-2 text-sm font-semibold text-[#0a1f2e] transition hover:bg-[#0a1f2e] hover:text-white">
            Show all {countries.length} countries
          </button>
        </div>
      )}
      <NumberInfoDialog open={info.open} country={info.country} group={info.group} onEnquire={onEnquire}
        onOpenChange={(open) => setInfo((s) => ({ ...s, open }))} />
    </div>
  );
};

export default NumberCountryTable;
